<template>
    <ModalForm
        :progress-text="$t('common:savingProgress')"
        :submit-action="onSubmit"
        :submit-text="$t('delete.submit')"
        :success-text="$t('common:saved')"
        :title="showsWarning ? $t('delete.headline_warning') : $t('delete.headline')"
        no-actions
        @close="close"
    >
        <div v-if="reasons">
            <ul
                v-if="showsWarning"
                class="mb-5 ml-6 list-disc"
            >
                <li>{{ $t('delete.text_warning') }}</li>
                <li v-if="associatedCredits > 0">
                    <i18next :translation="$t('delete.text_credits')">
                        <template #credits>
                            <BadgeCredits
                                :badge-size="7"
                                :value="associatedCredits"
                                :with-hover-effect="false"
                            ></BadgeCredits>
                        </template>
                    </i18next>
                </li>
            </ul>
            <div v-else>
                <div class="mb-2">{{ $t('delete.text_general') }}</div>
                <InputContainer
                    v-slot="{ state }"
                    :label="$t('delete.selectLabel')"
                    :vuelidate="v$.reason"
                >
                    <Select
                        v-model="reason"
                        data-testid="modal-form-delete-document-select-reason"
                        :options="reasons"
                        :placeholder="$t('common:modal.report.selectPlaceholder')"
                        :state="state"
                    />
                </InputContainer>
                <InputContainer
                    v-if="showTextInput"
                    v-slot="{ state }"
                    :label="$t('common:modal.report.commentLabel')"
                    :vuelidate="v$.comment"
                >
                    <InputTextarea
                        v-model="comment"
                        data-testid="modal-form-delete-document-input-text"
                        :placeholder="$t('common:modal.report.commentPlaceholder')"
                        :state="state"
                        class="max-h-40"
                    />
                </InputContainer>
            </div>

            <div class="flex justify-end">
                <BtnCta
                    data-testid="modal-form-delete-document-cancel"
                    class="mr-4"
                    :label="$t('common:modal.common.cancelText')"
                    @click="close"
                />
                <BtnCta
                    data-testid="modal-form-delete-document-submit"
                    :variant="BtnCtaVariant.SECONDARY"
                    :color="showsWarning ? 'negative' : 'primary'"
                    :label="showsWarning ? $t('delete.submit') : $t('common:continue')"
                    :loading="isLoading"
                    @click="showsWarning ? onSubmit() : onContinue()"
                />
            </div>
        </div>
    </ModalForm>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import ModalMixin from '@/mixins/modalMixin';
import Select from '@/components/Select';
import ModalForm from '@/components/ModalForm';
import InputContainer from '@components/InputContainer/InputContainer.vue';
import BadgeCredits from '@components/BadgeCredits/BadgeCredits.vue';
import InputTextarea from '@components/InputTextarea/InputTextarea.vue';
import BtnCta from '@components/BtnCta/BtnCta.vue';
import { maxLength, minLength, required, requiredIf } from '@/utils/validators';
import { BtnCtaVariant } from '@/components/BtnCta';
import { deleteDocument } from '@/api/backend/document';
import { getAssociatedCredits, getDeleteReasons } from '@/api/gateway/legacy-api';
import { storeDeleteReason } from '@/api/gateway/legacy-api/document';
import route from '@/router/route';
import type { SuccessResponse, DataResponse } from '@/api/types';

const DELETE_REASONS = {
    WRONG_COURSE: 1,
    MISTAKE: 2,
    BAD_QUALITY: 3,
    DONT_ASSOCIATE: 4,
    DONT_WANT_TO_SHARE: 5,
    OTHER: 6,
};

type ReasonType = { label: string; value: number; requireComment: boolean };
type ReasonResponse = { id: number; type: string; translation: string };
type AssociatedCreditsResponse = { total_associated_credits: number };

export default defineComponent({
    name: 'ModalFormDeleteDocument',
    components: {
        BtnCta,
        InputTextarea,
        BadgeCredits,
        InputContainer,
        ModalForm,
        Select,
    },
    extends: ModalMixin,
    i18nOptions: {
        namespaces: ['document'],
    },
    props: {
        /**
         * Document ID as number
         */
        documentId: {
            type: Number,
            required: true,
        },
    },
    setup() {
        return {
            BtnCtaVariant,
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            isLoading: false,
            showsWarning: false,
            comment: '',
            reason: null as unknown as ReasonType,
            reasons: null as unknown as ReasonType[],
            associatedCredits: 0,
        };
    },
    computed: {
        showTextInput(): boolean {
            return this.reason?.value === DELETE_REASONS.OTHER;
        },
        /**
         * The 'comment' default value had to be changed from null to
         * empty string. Not sure that if that would break anything with
         * the storeDeleteReason() or deleteDocument(). Therefore, the computed
         * property below is added.
         */
        getComment(): string | null {
            return this.comment === '' ? null : this.comment;
        },
    },
    async created() {
        const reasons = (await getDeleteReasons('document')) as unknown as ReasonResponse[];
        this.reasons = Object.values(reasons).map((reason) => ({
            value: reason.id,
            label: reason.translation,
            requireComment: reason.id === DELETE_REASONS.OTHER,
        }));

        const data = (await getAssociatedCredits(this.documentId)) as unknown as AssociatedCreditsResponse;
        this.associatedCredits = data.total_associated_credits ?? 0;
    },
    validations() {
        return {
            reason: {
                required,
            },
            comment: {
                required: requiredIf(this.reason?.requireComment),
                minLength: minLength(3),
                maxLength: maxLength(255),
            },
        };
    },
    methods: {
        onContinue(): void {
            this.v$.$touch();

            if (this.v$.$invalid) return;
            if (this.isLoading) return;

            storeDeleteReason(this.documentId, this.reason?.value, this.getComment);

            this.showsWarning = true;
        },
        async onSubmit() {
            this.isLoading = true;

            const response = (await deleteDocument(
                this.documentId,
                this.reason.value,
                this.getComment,
            )) as DataResponse<SuccessResponse>;

            if (!response.data.success) {
                await this.$store.dispatch('flashMessages/showError', {
                    content: this.$t('delete.error'),
                    progress: { time: 3000, showProgress: false },
                });
                this.isLoading = false;

                return false;
            }

            await this.$store.dispatch('flashMessages/showSuccess', {
                content: this.$t('delete.success'),
                progress: { time: 3000, showProgress: false },
            });
            this.isLoading = false;
            this.$modal.close();

            window.location.href = route('profile.study-material.list', { type: 'documents' });

            return true;
        },
    },
});
</script>
